import { isRole } from '@studio/access';
import { CLIENT_NAME, DEVICE_ID } from '@studio/api/constants';
import { HttpError } from '@studio/api/errors';
import type { AnyResource, DataDocument } from '@studio/api/json-api';
import { apiURL, createApiURL, fetchWrapper } from '@studio/api/utils';
import { createRetryStrategy } from '@studio/api/utils/retryStrategy';
import type { ExperienceConfig } from '@studio/core/utils/experience';
import type {
  PermissionGroup,
  PermissionGroups,
} from '@studio/core/utils/permission';
import acquireLock, { type LockGuard } from '@studio/utils/acquireLock';
import { encodeBase64Url } from '@studio/utils/base64';
import { DEFAULT_ROWS_PER_PAGE } from '@studio/utils/constants';
import createBroadcastStore from '@studio/utils/createBroadcastStore';
import type { RequestContext } from '@studio/utils/requestContext';

import {
  type CreateSNOWRequestsResponse,
  type GetSNOWRequestsResponse,
  type GetTeamsResponse,
  type IdentityTeam,
  isGetSnowAccessRequestsByTwIdResponse,
  isGetSnowRequestsResponse,
  isUserInGroup,
  type SNOWItem,
  type SNOWRequestPayload,
  type SnowRequestsByTwIdResponse,
  type StudioIdentityAccessRequestsParams,
  type StudioIdentityBasicUserInfo,
  type StudioIdentityToken,
  type StudioIdentityTokenPayloadWithToken,
  type StudioIdentityTokenResponse,
  type StudioIdentityUserInfo,
  type StudioIdentityUserInfoResponse,
  type StudioIdentityUsersByGroupRequest,
  type StudioIdentityUsersByGroupsRequest,
  type StudioIdentityUsersByRoleOrGroupResponse,
  type StudioIdentityUsersReportRequest,
  type StudioIdentityUsersReportResponse,
  type StudioIdentityUsersRequest,
  type StudioIdentityUsersResponse,
  type TeamMembersResponse,
  type UserDecoration,
  type UserId,
} from './types';
import {
  extractPublishingSite,
  getTokenPayload,
  isPublishingSiteRole,
  type SNOWTicketStage,
  type SNOWTicketState,
} from './utils';

export type * from './types';
export * from './utils';

export const sameDomainApiURL = createApiURL(
  false,
  new URL('/api/', window.location.origin),
);

// The broadcast aspect is not strictly necessary for correctness,
// but it prevents each tab from having to make its own request to get the token
export const tokenStore = createBroadcastStore<
  StudioIdentityTokenPayloadWithToken | null,
  StudioIdentityToken | null
>(
  'token',
  (state, token) => {
    if (token == null) {
      return null;
    }

    if (state?.token === token) {
      return state;
    }

    return {
      token,
      ...getTokenPayload(token),
    };
  },
  null,
);

// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
if (import.meta.webpackHot) {
  import.meta.webpackHot.dispose(() => {
    tokenStore[Symbol.dispose]();
  });
}

function getTokenIfNotExpired() {
  const tokenState = tokenStore.unsafe_getState();

  if (tokenState == null || tokenState.exp * 1000 < Date.now()) {
    return null;
  }

  return tokenState;
}

export interface LoginUrlOptions {
  mode?: 'hybrid' | 'modern';
  realm: string;
  discoClient: string;
  deviceId: string;
  successPath: string;
  errorPath: string;
  redirectOrigin: string;
}

const identityApi = {
  getLoginHref(
    options: Omit<LoginUrlOptions, 'discoClient' | 'deviceId'>,
  ): string {
    const url = sameDomainApiURL`/identity/v0/login`;
    url.searchParams.set(
      'config',
      encodeBase64Url(
        JSON.stringify({
          ...options,
          deviceId: DEVICE_ID,
          discoClient: CLIENT_NAME,
        } satisfies LoginUrlOptions),
      ),
    );
    return url.toString();
  },

  async getToken(
    signal: AbortSignal,
  ): Promise<StudioIdentityTokenPayloadWithToken> {
    signal.throwIfAborted();

    // Return the local token right away, if it's not expired
    {
      const tokenState = getTokenIfNotExpired();
      if (tokenState != null) {
        return tokenState;
      }
    }

    // Attempt to acquire an exclusive lock to refresh the token.
    // If it's not available, it means someone else is already refreshing the token,
    // so we can wait for them to finish and then use the updated token.
    let exclusiveLock: LockGuard | null;
    while (
      (exclusiveLock = await acquireLock(tokenStore.name, {
        mode: 'exclusive',
        ifAvailable: true,
      })) == null
    ) {
      await using _sharedLock = await acquireLock(tokenStore.name, {
        mode: 'shared',
        signal,
      });

      const tokenState = getTokenIfNotExpired();
      if (tokenState != null) {
        return tokenState;
      }
    }

    // If we reached this point, we have the exclusive lock
    await using lock = exclusiveLock;

    const url = sameDomainApiURL`/identity/v0/token`;

    for (let attempt = 0; ; attempt += 1) {
      const response = await fetchWrapper(url, {
        method: 'POST',
        signal: lock.signal,
        retryStrategy: createRetryStrategy({
          additionalRetryableMethods: ['POST'],
        }),
        // Avoid recursive overflow
        skipInterceptors: true,
        body: JSON.stringify({
          forceRefresh: attempt > 0,
        }),
      });

      const { token }: StudioIdentityTokenResponse = await response.json();

      tokenStore.dispatch(token);

      const tokenState = getTokenIfNotExpired();

      if (tokenState != null) {
        return tokenState;
      }
    }
  },

  async revokeToken(context: RequestContext): Promise<void> {
    const { signal } = context;

    signal.throwIfAborted();

    const url = sameDomainApiURL`/identity/v0/token`;

    await using lock = await acquireLock(tokenStore.name, { signal });

    try {
      await fetchWrapper(url, {
        ...context,
        signal: lock.signal,
        method: 'DELETE',
        skipRetries: true,
        skipInterceptors: true,
      });
    } finally {
      tokenStore.dispatch(null);
    }
  },

  async createMasqueradeSession(
    context: RequestContext,
    roles: readonly string[],
  ): Promise<void> {
    const url = sameDomainApiURL`identity/v0/masquerade`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'POST',
      body: JSON.stringify({ roles }),
    });
    const { token }: StudioIdentityTokenResponse = await response.json();

    tokenStore.dispatch(token);
  },

  // We could instead use getToken with forceRefresh and include that as an arg,
  // but it would require some refactoring of restoreSession and/or getToken
  async revokeMasqueradeSession(context: RequestContext | null): Promise<void> {
    const url = sameDomainApiURL`/identity/v0/token`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'POST',
      body: JSON.stringify({ forceRefresh: true }),
    });

    const { token }: StudioIdentityTokenResponse = await response.json();

    tokenStore.dispatch(token);
  },

  async revokeSonicToken(context: RequestContext | null): Promise<void> {
    const url = sameDomainApiURL`/identity/v0/sonic-token`;

    await fetchWrapper(url, {
      ...context,
      method: 'DELETE',
      skipRetries: true,
      skipInterceptors: true,
    });
  },

  async getExperienceConfig(
    context: RequestContext,
  ): Promise<ExperienceConfig> {
    const url = apiURL`/identity/v0/experience-config`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    const { config } = await response.json();

    return config;
  },

  async getCustomAttributes(
    context: RequestContext,
    customAttributesId: string,
  ): Promise<unknown> {
    const url = apiURL`/identity/v0/custom-attributes/${customAttributesId}`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    const document: DataDocument<AnyResource> = await response.json();

    return document.data.attributes;
  },

  async setCustomAttributes(
    context: RequestContext,
    customAttributesId: string,
    customAttributes: unknown,
  ): Promise<void> {
    const url = apiURL`/identity/v0/custom-attributes/${customAttributesId}`;

    await fetchWrapper(url, {
      ...context,
      method: 'PUT',
      body: JSON.stringify({
        data: {
          type: 'userCustomAttributes',
          id: customAttributesId,
          attributes: customAttributes,
        },
      }),
    });
  },

  async getUserInfo(
    context: RequestContext,
    userId: UserId,
  ): Promise<StudioIdentityUserInfo | null> {
    const url = apiURL`/identity/v0/users/${userId}`;

    try {
      const response = await fetchWrapper(url, {
        ...context,
        method: 'GET',
      });

      const json: StudioIdentityUserInfoResponse = await response.json();

      return {
        ...json,
        roles: json.roles.filter(isRole).sort(),
        publishingSites: json.roles
          .filter(isPublishingSiteRole)
          .map(extractPublishingSite),
      };
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async getUserDecoration(
    context: RequestContext,
    email: string,
  ): Promise<UserDecoration> {
    const url = apiURL`/identity/v0/users/${email}/decoration`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    return await response.json();
  },

  async getUsers(
    context: RequestContext,
    request: StudioIdentityUsersRequest,
  ): Promise<StudioIdentityUsersResponse> {
    const url = apiURL`/identity/v0/users`;

    for (const [key, value] of Object.entries(request)) {
      url.searchParams.set(key, value);
    }

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    return await response.json();
  },

  async getUsersReport(
    context: RequestContext,
    request: StudioIdentityUsersReportRequest,
  ): Promise<StudioIdentityUsersReportResponse> {
    const url = apiURL`/identity/v0/users-report`;

    for (const [key, value] of Object.entries(request)) {
      url.searchParams.set(key, value);
    }

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    return await response.json();
  },

  async getUsersByRoleOrPermissionGroup(
    context: RequestContext,
    request: StudioIdentityUsersByGroupRequest,
  ): Promise<StudioIdentityUsersByRoleOrGroupResponse> {
    const { permissionGroup, query } = request;

    const url = apiURL`/identity/v0/roles/${permissionGroup}/users`;

    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value);
    }
    try {
      const response = await fetchWrapper(url, {
        ...context,
        method: 'GET',
      });
      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          users: [],
          meta: {
            page: 0,
            pageSize: 10,
            totalItems: 0,
          },
        };
      }
      throw error;
    }
  },

  async getPermissionGroups(
    context: RequestContext,
  ): Promise<PermissionGroups> {
    const url = apiURL`/identity/v0/permission-groups`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'GET',
    });

    const permissionGroupArray: readonly PermissionGroup[] =
      await response.json();

    return new Map(
      permissionGroupArray.map(permissionGroup => [
        permissionGroup.name,
        permissionGroup,
      ]),
    );
  },

  async getUserDetailsByTwId(
    context: RequestContext,
    twId: string,
  ): Promise<StudioIdentityBasicUserInfo | null> {
    const url = apiURL`/identity/v0/users/twid/${twId}`;

    try {
      const response = await fetchWrapper(url, {
        ...context,
        method: 'GET',
      });

      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return null;
      }

      throw error;
    }
  },

  async getTeams(context: RequestContext): Promise<GetTeamsResponse> {
    const url = apiURL`/identity/v0/teams`;

    try {
      const response = await fetchWrapper(url, context);
      return await response.json();
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          teams: [],
          meta: {
            page: 0,
            pageSize: 0,
            totalItems: 0,
          },
        };
      }
      throw error;
    }
  },

  async getTeamsDetails(
    context: RequestContext,
    teamIds?: readonly string[],
  ): Promise<IdentityTeam[]> {
    if (teamIds == null || teamIds.length === 0) {
      return [];
    }
    const teamPromises = teamIds.map(teamId =>
      identityApi.getTeamById(context, teamId),
    );

    const teams = (await Promise.all(teamPromises)).filter(
      (team): team is IdentityTeam => team != null,
    );

    return teams;
  },

  async getTeamById(
    context: RequestContext,
    teamId: string,
  ): Promise<IdentityTeam | null> {
    const url = apiURL`/identity/v0/teams/${encodeURIComponent(teamId)}`;

    try {
      const response = await fetchWrapper(url, context);

      const body = await response.json();

      return body.team satisfies IdentityTeam;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return null;
      }

      throw error;
    }
  },

  async getTeamMembers(
    context: RequestContext,
    teamId: string,
  ): Promise<TeamMembersResponse> {
    const url = apiURL`/identity/v0/teams/${encodeURIComponent(teamId)}/members`;

    try {
      const response = await fetchWrapper(url, context);

      const body = await response.json();

      if (!isUserInGroup(body)) {
        throw new TypeError('Invalid team members type response');
      }

      return body;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          users: [],
          meta: {
            page: 0,
            pageSize: 0,
            totalItems: 0,
          },
        };
      }

      throw error;
    }
  },

  /*
   * Fetches users by multiple permission groups, combining results into a single array.
   * This retrives all users from each permission group, with pagination handled.
   * As there is no API endpoint to fetch users by multiple permission groups at once,
   * we use Promise.all to process all permission groups in parallel.
   */
  async getUsersByRoleOrPermissionGroups(
    context: RequestContext,
    request: StudioIdentityUsersByGroupsRequest,
  ): Promise<StudioIdentityUserInfo[]> {
    const { permissionGroups } = request;
    const combinedUsers = new Map<UserId, StudioIdentityUserInfo>();

    // Helper function to fetch all users for a single permission group with pagination
    const fetchAllUsersForPermissionGroup = async (permissionGroup: string) => {
      const usersForGroup: StudioIdentityUserInfo[] = [];
      let page = 0;
      let hasMorePages = true;
      const pageSize = 100;

      // Fetch all pages for this permission group
      while (hasMorePages) {
        const result = await identityApi.getUsersByRoleOrPermissionGroup(
          context,
          {
            permissionGroup,
            query: {
              page,
              pageSize,
            },
          },
        );

        // Add users to the array
        usersForGroup.push(...result.users);

        // Check if there are more pages
        const { totalItems } = result.meta;
        const totalPages = Math.ceil(totalItems / pageSize);
        page++;
        hasMorePages = page < totalPages;
      }

      return usersForGroup;
    };

    // Process all permission groups in parallel
    const userArrays = await Promise.all(
      permissionGroups.map(fetchAllUsersForPermissionGroup),
    );

    // Combine results from all permission groups
    for (const users of userArrays) {
      for (const user of users) {
        if (!combinedUsers.has(user.id)) {
          combinedUsers.set(user.id, user);
        }
      }
    }

    return Array.from(combinedUsers.values());
  },

  async getSnowAccessRequests(
    context: RequestContext,
    request: StudioIdentityAccessRequestsParams,
  ): Promise<GetSNOWRequestsResponse | null> {
    const url = apiURL`/identity/v0/snow/requests`;

    for (const [key, value] of Object.entries(request)) {
      url.searchParams.set(key, value);
    }

    try {
      const response = await fetchWrapper(url, context);
      const body = await response.json();

      if (!isGetSnowRequestsResponse(body)) {
        throw new TypeError('Invalid SNOW response');
      }

      const result = body.result.map(item => ({
        ...item,
        stage: item.stage as SNOWTicketStage,
        state: item.state as SNOWTicketState,
      }));

      return {
        result,
        meta: body.meta,
      };
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          result: [],
          meta: {
            page: 0,
            pageSize: 0,
            totalItems: 0,
          },
        };
      }
      throw error;
    }
  },

  async getSnowAccessRequestsByRITM(
    context: RequestContext,
    RITM: string,
  ): Promise<GetSNOWRequestsResponse | null> {
    const url = apiURL`/identity/v0/snow/requests/${encodeURIComponent(RITM)}`;

    try {
      const response = await fetchWrapper(url, context);
      const body = await response.json();

      if (!isGetSnowRequestsResponse(body)) {
        throw new TypeError('Invalid SNOW response');
      }

      const snowRequest = body.result?.[0];

      if (snowRequest == null) {
        return {
          result: [],
          meta: {
            page: 0,
            pageSize: 0,
            totalItems: 0,
          },
        };
      }

      const snowItem = {
        catalogueItem: snowRequest.catalogueItem,
        ritmNumber: snowRequest.ritmNumber,
        openedBy: snowRequest.openedBy,
        requestedFor: snowRequest.requestedFor,
        requestedForEmail: snowRequest.requestedForEmail,
        shortDescription: snowRequest.shortDescription,
        stage: snowRequest.stage as SNOWTicketStage,
        state: snowRequest.state as SNOWTicketState,
        systemId: snowRequest.systemId,
        workNotes: snowRequest.workNotes,
        detailDescription: snowRequest.detailDescription,
        updatedOn: snowRequest.updatedOn,
        closedAt: snowRequest.closedAt,
        createdOn: snowRequest.createdOn,
        permissionGroupData: snowRequest.permissionGroupData,
        approvers: snowRequest.approvers,
        approverGroups: [
          ...new Set(
            snowRequest.approvers.map(approver => approver.approverGroupName),
          ),
        ].join(', '),
      } satisfies SNOWItem;

      return {
        result: [snowItem],
        meta: body.meta,
      };
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          result: [],
          meta: {
            page: 0,
            pageSize: 0,
            totalItems: 0,
          },
        };
      }
      throw error;
    }
  },

  async createSnowAccessRequest({
    context,
    payload,
  }: {
    context: RequestContext;
    payload: SNOWRequestPayload;
  }): Promise<CreateSNOWRequestsResponse | null> {
    const url = apiURL`/identity/v0/snow/requests`;

    const response = await fetchWrapper(url, {
      ...context,
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return await response.json();
  },

  async getSnowAccessRequestsByTwId(
    context: RequestContext,
    twId: string,
    meta: StudioIdentityAccessRequestsParams,
  ): Promise<SnowRequestsByTwIdResponse | null> {
    const url = apiURL`/identity/v0/snow/requests/twid/${encodeURI(twId)}`;

    for (const [key, value] of Object.entries(meta)) {
      url.searchParams.set(key, value);
    }

    try {
      const response = await fetchWrapper(url, {
        ...context,
      });

      const json = await response.json();

      if (!isGetSnowAccessRequestsByTwIdResponse(json)) {
        throw new TypeError('Invalid SNOW response');
      }

      return json;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        return {
          result: [],
          meta: {
            page: 0,
            pageSize: DEFAULT_ROWS_PER_PAGE,
            totalItems: 0,
          },
        };
      }
      throw error;
    }
  },
};

export default identityApi;
